import {useCallback, useEffect, useState} from 'react'
import {useIntl} from 'react-intl'
import {useDispatch, useSelector} from 'react-redux'
import {useSearchBoxDestinationPicker} from 'hooks/searchBox'
import {useNavigateToSearchWithTrigger} from 'hooks/useNavigateToSearch'
import {useSetDeviceGeolocationSearchParams} from 'hooks/useSetDeviceGeolocationSearchParams'
import {getSearchContext} from 'middleware/analytics/selectors'
import {getUserCity, getUserCountryCode} from 'modules/meta/selectors'
import {useSapi} from 'modules/sapiSearch/SapiProvider'
import {SearchTrigger} from 'modules/searchBox/types'

import {useBrandCapabilities} from '@vio/core/src/_web/brand/hooks/useBrandCapabilities'
import {useInView} from '@vio/core/src/_web/utils/browser/hooks/useInView'
import {TeamContextType} from '@vio/core/src/analytics/components/TeamContext'
import {trackEvent} from '@vio/core/src/analytics/modules/actions'
import {
  Action,
  AnalyticsContext,
  Category,
  Entity
} from '@vio/core/src/analytics/types/Events'
import {today, tomorrow} from '@vio/core/src/utils/date'
import {logger} from '@vio/core/src/utils/logging/logger'

import {getCitiesData, TranslatedCity} from '../utils'

type UseLocationTopDeals = {
  component: string
  team: TeamContextType['team']
}

export const useLocationTopDeals = (trackingPayload: UseLocationTopDeals) => {
  const [citiesList, setCitiesList] = useState<TranslatedCity[]>([])
  const [selectedCity, setSelectedCity] = useState<TranslatedCity>()
  const [isCitiesGroupActive, setIsCitiesGroupActive] = useState(true)
  const [isFetchingGeo, setIsFetchingGeo] = useState(false)
  const currentUserCity = useSelector(getUserCity)
  const userCountryCode = useSelector(getUserCountryCode)
  const {onNearbyPropertiesSuggestionSelected} = useSearchBoxDestinationPicker()
  const {setDeviceGeolocationSearchParams} =
    useSetDeviceGeolocationSearchParams()
  const {sapiClient} = useSapi()
  const {
    search: {homeTopDealsDestinations}
  } = useBrandCapabilities()
  const {formatMessage} = useIntl()
  const [alreadyTrackedCarousel, setAlreadyTrackedCarousel] = useState(false)
  const dispatch = useDispatch()
  const searchContext = useSelector(getSearchContext)

  const checkIn = today()
  const checkOut = tomorrow()
  const rooms = '2'

  const baseSearchParameters = {
    rooms,
    checkIn,
    checkOut,
    userSearch: '1' as const,
    homeSearch: '1' as const
  }
  const navigateToSearch = useNavigateToSearchWithTrigger(
    SearchTrigger.TopDealsTonightWidget
  )

  const getCurrentCitySuggestion = useCallback(
    async city => {
      try {
        const suggestions = await sapiClient.suggest(city, 1, ['place'])
        return suggestions[0]
      } catch (error) {
        logger.error(error)
      }
    },
    [sapiClient]
  )

  useEffect(() => {
    if (sapiClient) {
      // eslint-disable-next-line @typescript-eslint/no-extra-semi
      ;(async () => {
        const preparedList: TranslatedCity[] = getCitiesData(
          userCountryCode,
          homeTopDealsDestinations
        ).map(city => ({
          name: formatMessage(city.name),
          placeId: city.placeId
        }))

        if (currentUserCity) {
          const suggestion = await getCurrentCitySuggestion(currentUserCity)
          if (suggestion?.objectID) {
            const currentCityData = {
              // Use the localized name from SAPI (e.g. "תל אביב") instead of the
              // raw English userCity, so it matches the other, translated pills.
              name: suggestion.value ?? currentUserCity,
              placeId: suggestion.objectID
            }
            // Drop the hardcoded entry for the same place, if present, so the
            // user city isn't listed twice. Compare by placeId since names are
            // localized.
            const dedupedList = preparedList.filter(
              city => city.placeId !== currentCityData.placeId
            )
            dedupedList.unshift(currentCityData)
            setCitiesList(dedupedList)
            return
          }
        }

        setCitiesList(preparedList)
      })()
    }
  }, [currentUserCity, sapiClient, homeTopDealsDestinations])

  useEffect(() => {
    if (citiesList.length) {
      setSelectedCity(citiesList[0])
    }
  }, [citiesList])

  const {ref, inView} = useInView({triggerOnce: true, threshold: 0.5})

  useEffect(() => {
    if (inView && !alreadyTrackedCarousel) {
      dispatch(
        trackEvent({
          category: Category.System,
          entity: Entity.Carousel,
          action: Action.Displayed,
          ...trackingPayload,
          payload: {
            searchItem: {
              checkIn,
              checkOut,
              rooms
            }
          },
          analyticsContext: {
            [AnalyticsContext.SearchContext]: searchContext
          }
        })
      )
      setAlreadyTrackedCarousel(true)
    }
  }, [
    inView,
    searchContext,
    dispatch,
    alreadyTrackedCarousel,
    setAlreadyTrackedCarousel
  ])

  const togglePillsActivity = state => () => setIsCitiesGroupActive(state)

  const handleChangeLocation = async (index: number) => {
    if (citiesList[index].placeId === undefined) {
      setIsFetchingGeo(true)

      await setDeviceGeolocationSearchParams()

      setSelectedCity(citiesList.find(city => city.placeId === undefined))
      setIsFetchingGeo(false)
    } else {
      setSelectedCity(citiesList[index])
    }
    dispatch(
      trackEvent({
        category: Category.User,
        entity: Entity.Carousel,
        action: Action.Clicked,
        ...trackingPayload,
        payload: {
          index,
          name: citiesList[index].name,
          placeId: citiesList[index].placeId,
          checkIn,
          checkOut
        }
      })
    )
  }

  const onHotelClick = ({hotelId}: {hotelId: string}) =>
    // bookingHint=1 (show date confirmation tooltip) is used to open the search box for mobile and date confirmation tooltip on desktop
    // it was added as part of this task: https://app.shortcut.com/findhotel/story/154347/update-landing-experience-for-users-with-no-dates-default-dates-widget-traffic
    navigateToSearch({...baseSearchParameters, hotelId, bookingHint: '1'})

  const onSeeAllClick = () => {
    dispatch(
      trackEvent({
        category: Category.User,
        entity: Entity.CarouselSeeAll,
        action: Action.Clicked,
        ...trackingPayload,
        payload: {
          searchItem: {
            placeId: selectedCity.placeId,
            checkIn,
            checkOut,
            rooms: '2'
          }
        }
      })
    )
    if (selectedCity.placeId === undefined) {
      onNearbyPropertiesSuggestionSelected(true)
    } else {
      navigateToSearch({...baseSearchParameters, placeId: selectedCity.placeId})
    }
  }

  return {
    handleChangeLocation,
    onHotelClick,
    onSeeAllClick,
    citiesList,
    selectedCity,
    checkIn,
    checkOut,
    isFetchingGeo,
    isCitiesGroupActive,
    togglePillsActivity,
    ref
  }
}
